// A workspace's files as a folder tree: breadcrumbs, folders, search, drag-drop upload of files and
// whole folders, drag-drop moves, and a Move/Rename form for touch screens. The open folder is kept
// in the URL (?path=), so Back and a copied link land in the same folder.
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { api } from '../api';
import { ACCEPT, MAX_BYTES, allowedName, droppedFiles, formatSize, formatWhen, openVersion, pickedFiles, uploadWithProgress, type FileEntry, type PickedFile } from '../files';
import { baseName, contents, countUnder, crumbs, dirName, joinPath, moveTarget, search } from '../tree';

interface Perms {
  canDownload: boolean;
  canUpload: boolean;
  canManage: boolean;
}

type Item = { kind: 'file'; id: string; path: string } | { kind: 'folder'; path: string };
const MOVE_TYPE = 'application/x-arh-move';

const folderFromUrl = () => new URLSearchParams(location.search).get('path') ?? '';

export function WorkspaceFiles({ id, title, files, folders, perms, reload }: { id: string; title: string; files: FileEntry[]; folders: string[]; perms: Perms; reload: () => Promise<unknown> }) {
  const [folder, setFolder] = useState(folderFromUrl);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploads, setUploads] = useState<{ path: string; progress: number; error?: string }[]>([]);
  const [openVersions, setOpenVersions] = useState<Record<string, boolean>>({});
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [editing, setEditing] = useState<Item | null>(null);
  const [newFolder, setNewFolder] = useState(false);
  const [over, setOver] = useState<string | null>(null);
  const filePicker = useRef<HTMLInputElement>(null);
  const folderPicker = useRef<HTMLInputElement>(null);

  // A folder that no longer exists (moved or deleted elsewhere) falls back to the root.
  const exists = (p: string) => p === '' || folders.includes(p);
  const here = exists(folder) ? folder : '';

  useEffect(() => {
    const onPop = () => setFolder(folderFromUrl());
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, []);
  function go(path: string) {
    setQuery('');
    setConfirmDelete(null);
    setEditing(null);
    setFolder(path);
    const url = new URL(location.href);
    if (path) url.searchParams.set('path', path);
    else url.searchParams.delete('path');
    history.pushState(null, '', url);
  }

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  /** Upload into `target`, keeping each file's relative folder. Skips types the server refuses. */
  async function uploadAll(picked: PickedFile[], target: string) {
    setError(null);
    const skipped = picked.filter((p) => !allowedName(p.file.name));
    const ok = picked.filter((p) => allowedName(p.file.name));
    setNotice(skipped.length ? `Skipped ${skipped.length} file${skipped.length > 1 ? 's' : ''} of a type that cannot be stored: ${skipped.slice(0, 4).map((s) => s.file.name).join(', ')}${skipped.length > 4 ? '…' : ''}` : null);
    for (const { file, path: rel } of ok) {
      const path = joinPath(target, rel);
      if (file.size > MAX_BYTES) {
        setUploads((u) => [...u, { path, progress: 0, error: 'Larger than 95 MB.' }]);
        continue;
      }
      setUploads((u) => [...u.filter((x) => x.path !== path), { path, progress: 0 }]);
      try {
        await uploadWithProgress(`/api/workspaces/${id}/files?name=${encodeURIComponent(path)}`, file, (p) => setUploads((u) => u.map((x) => (x.path === path ? { ...x, progress: p } : x))));
        setUploads((u) => u.filter((x) => x.path !== path));
      } catch (err) {
        setUploads((u) => u.map((x) => (x.path === path ? { ...x, error: (err as Error).message } : x)));
      }
    }
    await reload();
  }

  function move(item: Item, to: string) {
    return act(() =>
      item.kind === 'file' ? api(`/api/files/${item.id}`, { method: 'PATCH', body: { name: to } }) : api(`/api/workspaces/${id}/folders/move`, { body: { from: item.path, to } }),
    );
  }

  // Drag and drop: our own rows carry MOVE_TYPE; anything else with files is an upload from the desktop.
  const dragStart = (item: Item) => (e: DragEvent) => {
    e.dataTransfer.setData(MOVE_TYPE, JSON.stringify(item));
    e.dataTransfer.effectAllowed = 'move';
  };
  const canDrop = (e: DragEvent) => (e.dataTransfer.types.includes(MOVE_TYPE) ? perms.canManage : e.dataTransfer.types.includes('Files') && perms.canUpload);
  const dropTarget = (target: string) => ({
    onDragOver: (e: DragEvent) => {
      if (!canDrop(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setOver(target);
    },
    onDragLeave: () => setOver((o) => (o === target ? null : o)),
    onDrop: (e: DragEvent) => {
      if (!canDrop(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setOver(null);
      const raw = e.dataTransfer.getData(MOVE_TYPE);
      if (raw) {
        const item = JSON.parse(raw) as Item;
        const to = moveTarget(item.kind, item.path, target);
        if (to) void move(item, to);
        return;
      }
      void droppedFiles(e.dataTransfer).then((picked) => uploadAll(picked, target));
    },
  });

  const searching = query.trim() !== '';
  const view = searching ? search(files, folders, query) : contents(files, folders, here);
  const allFolders = ['', ...folders];

  return (
    <div className="list" data-testid="workspace-files">
      <div className="file-navigation">
      <div className="row">
        <h2 className="h2" style={{ flex: 1, margin: 0 }}>
          Files
        </h2>
        <input className="field" style={{ maxWidth: 240 }} type="search" aria-label="Search all folders" placeholder="Search all folders" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="file-search" />
      </div>

      <nav className="crumbs" aria-label="Folder" data-testid="crumbs">
        {crumbs(here, title).map((c, i, all) => (
          <span key={c.path} className="row" style={{ gap: 2 }}>
            {i > 0 && <span className="sep">/</span>}
            <button type="button" className={over === c.path ? 'drop-over' : undefined} aria-current={i === all.length - 1 && !searching ? 'page' : undefined} onClick={() => go(c.path)} {...dropTarget(c.path)} data-testid="crumb">
              {c.label}
            </button>
          </span>
        ))}
      </nav>
      </div>

      {error && <div className="error">{error}</div>}
      {notice && <div className="notice">{notice}</div>}

      {perms.canUpload && !searching && (
        <>
          <div className={`dropzone${over === `zone:${here}` ? ' drop-over' : ''}`} {...dropTarget(here)} onDragOver={(e) => (canDrop(e) ? (e.preventDefault(), setOver(`zone:${here}`)) : undefined)} onDragLeave={() => setOver(null)} data-testid="dropzone">
            <strong>Drop files or folders here</strong>
            <span className="muted small">into {here ? `“${baseName(here)}”` : 'the top level'} · up to 95 MB each · same name = new version · folders keep their structure</span>
            <div className="row" style={{ justifyContent: 'center', marginTop: 4 }}>
              <button type="button" className="btn sm" onClick={() => filePicker.current?.click()}>
                Choose files
              </button>
              <button type="button" className="btn sm" onClick={() => folderPicker.current?.click()} data-testid="upload-folder">
                Upload a folder
              </button>
              <button type="button" className="btn sm" onClick={() => setNewFolder(true)} data-testid="new-folder">
                New folder
              </button>
            </div>
            <input
              ref={filePicker}
              type="file"
              multiple
              accept={ACCEPT}
              hidden
              data-testid="file-input"
              onChange={(e) => {
                if (e.target.files) void uploadAll(pickedFiles(e.target.files), here);
                e.target.value = '';
              }}
            />
            <input
              ref={folderPicker}
              type="file"
              hidden
              data-testid="folder-input"
              {...{ webkitdirectory: '', directory: '' }}
              onChange={(e) => {
                if (e.target.files) void uploadAll(pickedFiles(e.target.files), here);
                e.target.value = '';
              }}
            />
          </div>
          {newFolder && (
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                const name = String(new FormData(e.currentTarget).get('name') ?? '').trim();
                if (!name) return;
                void act(() => api(`/api/workspaces/${id}/folders`, { body: { path: joinPath(here, name) } })).then(() => setNewFolder(false));
              }}
            >
              <input name="name" className="field" style={{ flex: 1, minWidth: 160 }} placeholder="Folder name, e.g. Lampiran A" maxLength={80} autoFocus data-testid="new-folder-name" />
              <button className="btn primary sm">Create folder</button>
              <button type="button" className="btn ghost sm" onClick={() => setNewFolder(false)}>
                Cancel
              </button>
            </form>
          )}
        </>
      )}
      {uploads.map((u) => (
        <div key={u.path} className={u.error ? 'error' : 'notice'} data-testid="upload-status">
          {u.path}: {u.error ?? `${Math.round(u.progress * 100)}%`}
        </div>
      ))}

      {searching && <div className="muted small">{view.folders.length + view.files.length} match{view.folders.length + view.files.length === 1 ? '' : 'es'} in all folders</div>}
      {!searching && here && (
        <div className="list-item folder-row" onClick={() => go(dirName(here))} {...dropTarget(dirName(here))} data-testid="folder-up">
          <span className="icon">↑</span>
          <span className="muted">Up to {dirName(here) ? baseName(dirName(here)) : title}</span>
        </div>
      )}
      {!searching && view.folders.length === 0 && view.files.length === 0 && <div className="muted small">{here ? 'This folder is empty.' : 'No files yet.'}</div>}

      {view.folders.map((p) => (
        <div
          key={p}
          className={`list-item folder-row${over === p ? ' drop-over' : ''}`}
          draggable={perms.canManage}
          onDragStart={dragStart({ kind: 'folder', path: p })}
          onClick={() => go(p)}
          {...dropTarget(p)}
          data-testid="folder-row"
        >
          <span className="icon" aria-hidden>
            📁
          </span>
          <div style={{ flex: 1, minWidth: 140 }}>
            <strong>{baseName(p)}</strong>
            {searching && dirName(p) && <div className="path">{dirName(p)}</div>}
            <div className="muted small">
              {countUnder(files, p)} file{countUnder(files, p) === 1 ? '' : 's'}
            </div>
          </div>
          {perms.canManage && (
            <div className="row" onClick={(e) => e.stopPropagation()}>
              <button className="btn ghost sm" onClick={() => setEditing({ kind: 'folder', path: p })}>
                Move / rename
              </button>
              {confirmDelete === p ? (
                <button className="btn danger sm" onClick={() => act(() => api(`/api/workspaces/${id}/folders?path=${encodeURIComponent(p)}`, { method: 'DELETE' })).then(() => setConfirmDelete(null))}>
                  Confirm: folder and its files to the trash
                </button>
              ) : (
                <button className="btn ghost sm" onClick={() => setConfirmDelete(p)}>
                  Delete
                </button>
              )}
            </div>
          )}
          {editing?.kind === 'folder' && editing.path === p && <MoveForm item={editing} folders={allFolders} title={title} onCancel={() => setEditing(null)} onSave={(to) => move(editing, to).then(() => setEditing(null))} />}
        </div>
      ))}

      {view.files.map((f) => (
        <div key={f.id} className="list-item" data-testid="file-row" style={{ alignItems: 'flex-start' }} draggable={perms.canManage} onDragStart={dragStart({ kind: 'file', id: f.id, path: f.name })}>
          <span className="file-icon" aria-hidden>
            📄
          </span>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div>
              <strong>{baseName(f.name)}</strong> {f.current && <span className="tag">v{f.current.version}</span>}
            </div>
            {searching && dirName(f.name) && (
              <button className="btn ghost sm path" style={{ padding: 0 }} onClick={() => go(dirName(f.name))}>
                in {dirName(f.name)}
              </button>
            )}
            {f.current && (
              <div className="muted small">
                {formatSize(f.current.size)} · {f.current.uploaded_by_kind === 'guest' ? `from guest ${f.current.uploaded_by_name}` : f.current.uploaded_by_name} · {formatWhen(f.current.uploaded_at)}
              </div>
            )}
            {f.versions.length > 1 && (
              <button className="btn ghost sm" style={{ paddingLeft: 0 }} onClick={() => setOpenVersions((o) => ({ ...o, [f.id]: !o[f.id] }))}>
                {openVersions[f.id] ? 'Hide' : 'Show'} {f.versions.length - 1} older version{f.versions.length > 2 ? 's' : ''}
              </button>
            )}
            {openVersions[f.id] &&
              f.versions.slice(1).map((v) => (
                <div key={v.id} className="row small muted" style={{ marginTop: 4 }}>
                  <span className="tag" style={{ opacity: 0.7 }}>
                    v{v.version} superseded
                  </span>
                  {formatSize(v.size)} · {v.uploaded_by_name} · {formatWhen(v.uploaded_at)}
                  {perms.canDownload && (
                    <button className="btn ghost sm" onClick={() => act(() => openVersion(`/api/versions/${v.id}/link`, true))}>
                      Download
                    </button>
                  )}
                  {perms.canManage && (
                    <button className="btn ghost sm" onClick={() => act(() => api(`/api/versions/${v.id}`, { method: 'DELETE' }))}>
                      Delete
                    </button>
                  )}
                </div>
              ))}
            {editing?.kind === 'file' && editing.id === f.id && <MoveForm item={editing} folders={allFolders} title={title} onCancel={() => setEditing(null)} onSave={(to) => move(editing, to).then(() => setEditing(null))} />}
          </div>
          {f.current && (perms.canDownload || perms.canManage) && (
            <div className="row">
              {perms.canDownload && f.inline && (
                <button className="btn sm" onClick={() => act(() => openVersion(`/api/versions/${f.current!.id}/link`, false))}>
                  Open
                </button>
              )}
              {perms.canDownload && (
                <button className="btn sm" onClick={() => act(() => openVersion(`/api/versions/${f.current!.id}/link`, true))}>
                  Download
                </button>
              )}
              {perms.canManage && (
                <button className="btn ghost sm" onClick={() => setEditing({ kind: 'file', id: f.id, path: f.name })} data-testid="move-file">
                  Move / rename
                </button>
              )}
              {perms.canManage &&
                (confirmDelete === f.id ? (
                  <button className="btn danger sm" onClick={() => act(() => api(`/api/files/${f.id}`, { method: 'DELETE' })).then(() => setConfirmDelete(null))}>
                    Confirm delete
                  </button>
                ) : (
                  <button className="btn ghost sm" onClick={() => setConfirmDelete(f.id)}>
                    Delete
                  </button>
                ))}
            </div>
          )}
        </div>
      ))}
      {perms.canManage && (files.length > 0 || folders.length > 0) && <div className="muted small">Drag a file or folder onto a folder or a breadcrumb to move it.</div>}
    </div>
  );
}

/** Move and rename in one: pick the destination folder, edit the name. Works without drag (phones). */
function MoveForm({ item, folders, title, onSave, onCancel }: { item: Item; folders: string[]; title: string; onSave: (to: string) => Promise<unknown>; onCancel: () => void }) {
  // A folder cannot move into itself or below itself.
  const choices = item.kind === 'folder' ? folders.filter((p) => p !== item.path && !p.startsWith(`${item.path}/`)) : folders;
  return (
    <form
      className="row"
      style={{ width: '100%', marginTop: 6 }}
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => {
        e.preventDefault();
        const f = new FormData(e.currentTarget);
        const name = String(f.get('name') ?? '').trim();
        if (name) void onSave(joinPath(String(f.get('folder') ?? ''), name));
      }}
      data-testid="move-form"
    >
      <select name="folder" className="field" style={{ flex: 1, minWidth: 140 }} defaultValue={dirName(item.path)} aria-label="Folder">
        {choices.map((p) => (
          <option key={p} value={p}>
            {p ? `${title} / ${p.replace(/\//g, ' / ')}` : `${title} (top level)`}
          </option>
        ))}
      </select>
      <input name="name" className="field" style={{ flex: 1, minWidth: 140 }} defaultValue={baseName(item.path)} maxLength={80} aria-label="Name" />
      <button className="btn primary sm">Save</button>
      <button type="button" className="btn ghost sm" onClick={onCancel}>
        Cancel
      </button>
    </form>
  );
}
